/* Related parties — shared helpers for the asset-finance people list.
 *
 * Exactly one party is the PRIMARY CONTACT. That matters well beyond this
 * step: the application row's applicant_email / applicant_phone /
 * applicant_first_name columns drive recovery emails, reply threading,
 * decline notices and duplicate detection. So whenever the list changes, the
 * primary's details are mirrored up to the top-level form fields — the same
 * fields a consumer application fills at step 1 — and every one of those
 * machines keeps working untouched.
 */

let seq = 0;
export const blankParty = (over = {}) => ({
  id: `p_${Date.now().toString(36)}_${seq++}`,
  relationship: "", isExistingCustomer: null,
  title: "", firstName: "", middleName: "", lastName: "",
  dateOfBirth: "", gender: "",
  mobile: "", homePhone: "", email: "", address: "", movedIn: "",
  creditHistoryIssues: null, creditHistoryDetail: "",
  bankrupt: null, bankruptDetail: "",
  overseasTax: null, overseasTaxDetail: "",
  isPrimary: false,
  ...over,
});

export const partyName = (p = {}) => [p.firstName, p.lastName].filter(Boolean).join(" ").trim();

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const MOBILE_RE = /^04\d{8}$/;
const DOB_RE = /^\d{4}-\d{2}-\d{2}$/;

const adult = (d) => {
  if (!DOB_RE.test(d || "")) return false;
  const t = new Date(d + "T00:00:00");
  if (Number.isNaN(t.getTime())) return false;
  const age = (Date.now() - t.getTime()) / (365.25 * 24 * 3600 * 1000);
  return age >= 18 && age <= 120;
};

/* What's still missing from a party, in plain words the applicant can act on.
   Empty array = complete. */
export function partyIssues(p = {}) {
  const need = [];
  if (!p.relationship) need.push("their relationship to the business");
  if ((p.firstName || "").trim().length < 2) need.push("first name");
  if ((p.lastName || "").trim().length < 2) need.push("last name");
  if (!adult(p.dateOfBirth)) need.push("a date of birth (they must be 18 or over)");
  if (!EMAIL_RE.test(p.email || "")) need.push("an email address");
  if (!MOBILE_RE.test((p.mobile || "").replace(/\s/g, ""))) need.push("an Australian mobile (04XX XXX XXX)");
  if ((p.address || "").trim().length < 6) need.push("a residential address");
  if (p.isExistingCustomer == null) need.push("whether they're an existing customer");
  if (p.creditHistoryIssues == null) need.push("their credit history answer");
  if (p.bankrupt == null) need.push("their bankruptcy answer");
  if (p.overseasTax == null) need.push("their overseas tax answer");
  return need;
}

export const partyComplete = (p) => partyIssues(p).length === 0;

/* Relationships that make someone a principal of the entity. A guarantor
   supports an application; they can't BE the application. */
const PRINCIPALS = new Set(["DIRECTOR", "SOLE_TRADER", "PARTNER", "TRUSTEE"]);

/* Whether the list as a whole can go forward, and why not. */
export function partiesIssues(parties = []) {
  if (!parties.length) return ["add at least one person"];
  const need = [];
  const incomplete = parties.filter((p) => !partyComplete(p));
  if (incomplete.length) need.push(`finish ${incomplete.map((p) => partyName(p) || "the new person").join(", ")}`);
  if (!parties.some((p) => PRINCIPALS.has(p.relationship))) {
    need.push("include at least one director, sole trader, partner or trustee - a guarantor alone isn't enough");
  }
  if (parties.filter((p) => p.isPrimary).length !== 1) need.push("choose exactly one main contact");
  return need;
}

/* The party whose details the platform uses to contact the applicant. */
export const primaryParty = (parties = []) => parties.find((p) => p.isPrimary) || parties[0] || null;

/* Mirror the primary contact up to the top-level lead fields. Those are what
   the draft store reads for applicant_email / applicant_phone /
   applicant_first_name, so this is what keeps recovery emails, reply
   threading and the queue pointed at a real person. Returns a patch. */
export function syncPrimaryToLead(parties = []) {
  const p = primaryParty(parties);
  if (!p) return {};
  return {
    firstName: p.firstName || "",
    lastName: p.lastName || "",
    dateOfBirth: p.dateOfBirth || "",
    email: p.email || "",
    phone: p.mobile || "",
    address: p.address || "",
  };
}

/* Seed the first party from what step 1 already captured, so the person
   filling the form isn't asked for their own name and email twice.
   The id is FIXED, not generated: the seed is recomputed on every render
   until it's saved, and a fresh id each time would make the save append a
   second copy of the same person instead of replacing the placeholder. */
export const SEED_PARTY_ID = "p_primary";
export function seedPartiesFromLead(form = {}) {
  return [blankParty({
    id: SEED_PARTY_ID,
    isPrimary: true,
    firstName: form.firstName || "", lastName: form.lastName || "",
    dateOfBirth: form.dateOfBirth || "", email: form.email || "",
    mobile: form.phone || "", address: form.address || "",
  })];
}
